import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchLeads, verifyLead } from "../services/api";
import { SESSION_KEY } from "./AdminLogin";

export default function AdminLeads() {
  const [leads, setLeads]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [scanning, setScanning]   = useState(false); // live camera in progress
  const [scanResult, setScanResult] = useState(null);
  const fileInputRef = useRef(null);
  const scannerRef   = useRef(null);
  const navigate     = useNavigate();

  useEffect(() => {
    if (!sessionStorage.getItem(SESSION_KEY)) { navigate("/admin"); return; }
    fetchLeads()
      .then(setLeads)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [navigate]);

  // ── helpers ─────────────────────────────────────────────────────────
  function openModal() {
    setScanResult(null);
    setScanning(false);
    setModalOpen(true);
  }

  function closeModal() {
    stopCamera();
    setModalOpen(false);
    setScanResult(null);
    setScanning(false);
  }

  function stopCamera() {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
  }

  async function resolveToken(text) {
    // text is either a full URL or a raw token
    const token = text.includes("/") ? text.split("/").pop() : text;
    try {
      const data = await verifyLead(token);
      setScanResult({ lead: data });
    } catch {
      setScanResult({ error: "QR code not recognised. Not a valid registration." });
    }
  }

  // ── File-upload scan (works on HTTP + iOS/Android) ───────────────────
  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";          // reset so same file can be re-selected
    setScanResult(null);

    // Html5Qrcode needs a rendered (non-display:none) element to draw a canvas into.
    // Create a temporary off-screen div so mobile browsers don't produce a 0×0 canvas.
    const tempId = "qr-scan-temp-" + Date.now();
    const tempEl = document.createElement("div");
    tempEl.id = tempId;
    tempEl.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:300px;height:300px;overflow:hidden";
    document.body.appendChild(tempEl);

    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const decoder = new Html5Qrcode(tempId);
      const text = await decoder.scanFile(file, false);
      await resolveToken(text);
    } catch {
      setScanResult({ error: "Could not read QR code from this image. Try a clearer photo." });
    } finally {
      document.body.removeChild(tempEl);
    }
  }

  // ── Live camera scan (works on localhost & HTTPS) ───────────────────
  async function startCamera() {
    setScanning(true);
    setScanResult(null);
  }

  useEffect(() => {
    if (!scanning) return;
    let stopped = false;

    import("html5-qrcode").then(({ Html5Qrcode }) => {
      if (stopped) return;
      const qr = new Html5Qrcode("qr-live-view");
      scannerRef.current = qr;
      qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (text) => {
          await qr.stop().catch(() => {});
          scannerRef.current = null;
          setScanning(false);
          await resolveToken(text);
        },
        () => {}
      ).catch((err) => {
        setScanning(false);
        setScanResult({ error: "Camera unavailable: " + err + "\n\nUse the 'Take Photo' option instead." });
      });
    });

    return () => {
      stopped = true;
      if (scannerRef.current) { scannerRef.current.stop().catch(() => {}); scannerRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  // ── UI ───────────────────────────────────────────────────────────────
  function handleLogout() {
    sessionStorage.removeItem(SESSION_KEY);
    navigate("/admin");
  }

  function formatDate(iso) {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }

  const valid   = scanResult?.lead;
  const invalid = scanResult?.error;

  return (
    <div className="admin-wrapper">
      <div className="admin-topbar">
        <a href="/"><img src="/logo.jpeg" alt="Uni Global" className="admin-logo" /></a>
        <div className="admin-topbar-right">
          <button className="btn-scan-qr" onClick={openModal}>Scan QR</button>
          <span className="admin-badge">{leads.length} Lead{leads.length !== 1 ? "s" : ""}</span>
          <button className="btn-logout" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <div className="admin-body">
        <h2 className="admin-title">Education Loan Leads</h2>
        {loading && <p className="admin-status">Loading leads…</p>}
        {error   && <div className="server-error">{error}</div>}
        {!loading && !error && leads.length === 0 && (
          <p className="admin-status">No leads submitted yet.</p>
        )}
        {!loading && leads.length > 0 && (
          <div className="table-scroll">
            <table className="leads-table">
              <thead>
                <tr>
                  <th>#</th><th>Full Name</th><th>Mobile</th><th>Email</th>
                  <th>Country</th><th>Loan Amount</th><th>Consent</th><th>Submitted At</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, i) => (
                  <tr key={lead.id}>
                    <td>{i + 1}</td>
                    <td>{lead.full_name}</td>
                    <td>{lead.mobile_number}</td>
                    <td>{lead.email}</td>
                    <td>{lead.study_country}</td>
                    <td>{lead.loan_amount}</td>
                    <td className={lead.consent ? "badge-yes" : "badge-no"}>
                      {lead.consent ? "Yes" : "No"}
                    </td>
                    <td>{formatDate(lead.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      {modalOpen && (
        <div className="scan-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="scan-modal">
            <button className="scan-close" onClick={closeModal}>✕</button>

            {/* Result */}
            {valid && <ValidResult lead={valid} onScanAgain={openModal} />}
            {invalid && <InvalidResult msg={invalid} onScanAgain={openModal} />}

            {/* Options — shown when no result yet */}
            {!valid && !invalid && (
              <>
                {/* Live camera (works on localhost / HTTPS) */}
                {scanning ? (
                  <>
                    <p className="scan-heading">Point camera at the QR code</p>
                    <div id="qr-live-view" className="qr-reader-box" />
                    <button className="btn-scan-cancel" onClick={() => { stopCamera(); setScanning(false); }}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <p className="scan-heading">Verify a student registration</p>
                    <div className="scan-options">
                      {/* Primary: file capture — works on HTTP + iOS/Android */}
                      <button
                        className="scan-option-btn scan-option-primary"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <span className="scan-option-icon">📷</span>
                        <span className="scan-option-label">Take Photo of QR</span>
                        <span className="scan-option-hint">Opens camera — works on any device</span>
                      </button>

                      {/* Secondary: live stream — localhost / HTTPS only */}
                      <button
                        className="scan-option-btn scan-option-secondary"
                        onClick={startCamera}
                      >
                        <span className="scan-option-icon">🎥</span>
                        <span className="scan-option-label">Live Camera Scan</span>
                        <span className="scan-option-hint">Works on this laptop only</span>
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ValidResult({ lead, onScanAgain }) {
  return (
    <div className="scan-result">
      <div className="verify-badge verify-badge--valid">✓ Valid Registration</div>
      <div className="scan-fields">
        <Row label="Enquiry ID"  value={lead.qr_token?.slice(0, 8).toUpperCase()} />
        <Row label="Name"        value={lead.full_name} />
        <Row label="Mobile"      value={lead.mobile_number} />
        <Row label="Email"       value={lead.email} />
        <Row label="Country"     value={lead.study_country} />
        <Row label="Loan Amount" value={lead.loan_amount} />
        <Row label="Submitted"   value={new Date(lead.created_at).toLocaleString("en-IN", {
          day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
        })} />
      </div>
      <button className="btn-scan-again" onClick={onScanAgain}>Scan Another</button>
    </div>
  );
}

function InvalidResult({ msg, onScanAgain }) {
  return (
    <div className="scan-result">
      <div className="verify-badge verify-badge--invalid">✗ Invalid QR Code</div>
      <p className="scan-error-msg">{msg}</p>
      <button className="btn-scan-again" onClick={onScanAgain}>Try Again</button>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="scan-field-row">
      <span className="scan-field-label">{label}</span>
      <span className="scan-field-value">{value}</span>
    </div>
  );
}
